class Morph {
  constructor(options = {}) {
    this.options = {
      ...this.defaultOptions,
      ...options,
    };
  }
  get defaultOptions() {
    return {
      childrenOnly: true,
      reject(oldNode, newNode) {
        if (newNode.nodeType === Node.TEXT_NODE && newNode.nodeValue?.trim() === '') {
          return true;
        }

        if (
          newNode instanceof HTMLTemplateElement &&
          newNode.shadowRootMode === 'open' &&
          oldNode.parentElement &&
          newNode.parentElement &&
          oldNode.parentElement.tagName === newNode.parentElement.tagName &&
          oldNode.parentElement?.shadowRoot != null
        ) {
          return true;
        }

        if (newNode.nodeType === Node.COMMENT_NODE && newNode.nodeValue === 'shopify:rendered_by_section_api') {
          return true;
        }

        return false;
      },
      onBeforeUpdate(oldNode, newNode) {
        if (oldNode instanceof Element && newNode instanceof Element) {
          const attributes = ['product-grid-view'];

          for (const attribute of attributes) {
            const oldValue = oldNode.getAttribute(attribute);
            const newValue = newNode.getAttribute(attribute);

            if (oldValue && oldValue !== newValue) {
              newNode.setAttribute(attribute, oldValue);
            }
          }

          const elements = ['floating-panel-component'];
          const ids = ['account-popover'];

          for (const element of elements) {
            const tagName = element.toUpperCase();
            if (oldNode.tagName === tagName && newNode.tagName === tagName) {
              const oldStyle = oldNode.getAttribute('style');

              if (oldStyle) newNode.setAttribute('style', oldStyle);
            }
          }
          for (const id of ids) {
            if (oldNode.id === id && newNode.id === id) {
              const oldStyle = oldNode.getAttribute('style');
              if (oldStyle) newNode.setAttribute('style', oldStyle);
            }
          }

          if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement && oldNode.style.viewTransitionName) {
            newNode.style.viewTransitionName = oldNode.style.viewTransitionName;
          }
        }
      },
      onAfterUpdate(node) {
        if (node && typeof node.updatedCallback === 'function') {
          queueMicrotask(() => node.updatedCallback());
        }
      },
    };
  }
  static morph(oldTree, newTree, options = {}) {
    return new Morph(options).morph(oldTree, newTree);
  }
  morph(oldTree, newTree) {
    if (!oldTree || !newTree) {
      throw new Error('Both oldTree and newTree must be provided');
    }

    if (typeof newTree === 'string') {
      const parsedNewTree = new DOMParser().parseFromString(newTree, 'text/html').body.firstChild;
      if (!parsedNewTree) {
        throw new Error('newTree string is not valid HTML');
      }
      newTree = parsedNewTree;
    }

    if (this.options.childrenOnly) {
      this.updateChildren(newTree, oldTree);
      return oldTree;
    }

    if (newTree.nodeType === 11) {
      throw new Error('newTree should have one root node (not a DocumentFragment)');
    }

    return this.walk(newTree, oldTree);
  }
  walk(newNode, oldNode) {
    if (!oldNode) return newNode;
    if (!newNode) return oldNode;
    if (newNode.isSameNode?.(oldNode)) return oldNode;

    if (newNode.nodeType !== oldNode.nodeType) return newNode;
    if (newNode instanceof Element && oldNode instanceof Element) {
      if (oldNode.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT-CART') return oldNode;

      if (newNode.tagName !== oldNode.tagName) return newNode;
      const newKey = this.getNodeKey(newNode);
      const oldKey = this.getNodeKey(oldNode);
      if (newKey && oldKey && newKey !== oldKey) return newNode;
    }

    if (
      oldNode instanceof Element &&
      oldNode.hasAttribute('data-skip-node-update') &&
      newNode instanceof Element &&
      newNode.hasAttribute('data-skip-node-update')
    ) {
      this.updateChildren(newNode, oldNode);
    } else {
      this.updateNode(newNode, oldNode);
      this.updateChildren(newNode, oldNode);
    }

    this.options.onAfterUpdate?.(newNode);

    return oldNode;
  }
  updateNode(newNode, oldNode) {
    this.options.onBeforeUpdate?.(oldNode, newNode);

    if (
      (newNode instanceof HTMLDetailsElement && oldNode instanceof HTMLDetailsElement) ||
      (newNode instanceof HTMLDialogElement && oldNode instanceof HTMLDialogElement)
    ) {
      if (!newNode.hasAttribute('declarative-open')) {
        newNode.open = oldNode.open;
      }
    }

    if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement) {
      for (const attr of ['slot', 'sizes']) {
        const oldValue = oldNode.getAttribute(attr);
        const newValue = newNode.getAttribute(attr);

        if (oldValue !== newValue) {
          oldValue == null ? newNode.removeAttribute(attr) : newNode.setAttribute(attr, oldValue);
        }
      }
    }

    if (newNode instanceof Element && oldNode instanceof Element) {
      if (!oldNode.isEqualNode(newNode)) {
        this.copyAttributes(newNode, oldNode);
      }
    } else if (newNode instanceof Text || newNode instanceof Comment) {
      if (oldNode.nodeValue !== newNode.nodeValue) {
        oldNode.nodeValue = newNode.nodeValue;
      }
    }

    // Handle special elements
    if (newNode instanceof HTMLInputElement && oldNode instanceof HTMLInputElement) {
      this.updateInput(newNode, oldNode);
    } else if (newNode instanceof HTMLOptionElement && oldNode instanceof HTMLOptionElement) {
      this.updateAttribute(newNode, oldNode, 'selected');
    } else if (newNode instanceof HTMLTextAreaElement && oldNode instanceof HTMLTextAreaElement) {
      this.updateTextarea(newNode, oldNode);
    }
  }
  getNodeKey(node) {
    return this.options?.getNodeKey?.(node) ?? (node instanceof Element ? node.id : undefined);
  }
  updateAttribute(newNode, oldNode, name) {
    if (newNode[name] !== oldNode[name]) {
      oldNode[name] = newNode[name];
      if (newNode[name] != null) {
        oldNode.setAttribute(name, '');
      } else {
        oldNode.removeAttribute(name);
      }
    }
  }
  copyAttributes(newNode, oldNode) {
    const oldAttrs = oldNode.attributes;
    const newAttrs = newNode.attributes;

    for (const attr of Array.from(newAttrs)) {
      const { name: attrName, namespaceURI: attrNamespaceURI, value: attrValue } = attr;
      const localName = attr.localName || attrName;

      if (attrName === 'src' || attrName === 'href' || attrName === 'srcset' || attrName === 'poster') {
        if (oldNode.getAttribute(attrName) === attrValue) continue;
      }

      if (attrNamespaceURI) {
        const fromValue = oldNode.getAttributeNS(attrNamespaceURI, localName);
        if (fromValue !== attrValue) {
          oldNode.setAttributeNS(attrNamespaceURI, localName, attrValue);
        }
      } else {
        if (!oldNode.hasAttribute(attrName)) {
          oldNode.setAttribute(attrName, attrValue);
        } else {
          const fromValue = oldNode.getAttribute(attrName);
          if (fromValue !== attrValue) {
            if (attrValue === 'null' || attrValue === 'undefined') {
              oldNode.removeAttribute(attrName);
            } else {
              oldNode.setAttribute(attrName, attrValue);
            }
          }
        }
      }
    }

    for (const attr of Array.from(oldAttrs)) {
      if (attr.specified === false) continue;

      const { name: attrName, namespaceURI: attrNamespaceURI } = attr;
      const localName = attr.localName || attrName;

      if (attrNamespaceURI) {
        if (!newNode.hasAttributeNS(attrNamespaceURI, localName)) {
          oldNode.removeAttributeNS(attrNamespaceURI, localName);
        }
      } else if (!newNode.hasAttribute(attrName)) {
        oldNode.removeAttribute(attrName);
      }
    }
  }
  updateInput(newNode, oldNode) {
    const newValue = newNode.value;

    this.updateAttribute(newNode, oldNode, 'checked');
    this.updateAttribute(newNode, oldNode, 'disabled');

    if (newNode.indeterminate !== oldNode.indeterminate) {
      oldNode.indeterminate = newNode.indeterminate;
    }

    if (oldNode.type === 'file') return;

    if (newValue !== oldNode.value) {
      oldNode.setAttribute('value', newValue);
      oldNode.value = newValue;
    }

    if (newValue === 'null') {
      oldNode.value = '';
      oldNode.removeAttribute('value');
    }

    if (!newNode.hasAttributeNS(null, 'value')) {
      oldNode.removeAttribute('value');
    } else if (oldNode.type === 'range') {
      oldNode.value = newValue;
    }
  }
  updateTextarea(newNode, oldNode) {
    const newValue = newNode.value;
    if (newValue !== oldNode.value) {
      oldNode.value = newValue;
    }

    const firstChild = oldNode.firstChild;
    if (firstChild?.nodeType === Node.TEXT_NODE) {
      if (newValue === '' && firstChild.nodeValue === oldNode.placeholder) {
        return;
      }
      firstChild.nodeValue = newValue;
    }
  }
  updateChildren(newNode, oldNode) {
    if (
      oldNode instanceof Element &&
      oldNode.hasAttribute('data-skip-subtree-update') &&
      newNode instanceof Element &&
      newNode.hasAttribute('data-skip-subtree-update')
    ) {
      return;
    }

    let oldChild, newChild, morphed, oldMatch;
    let offset = 0;

    for (let i = 0; ; i++) {
      oldChild = oldNode.childNodes[i];
      newChild = newNode.childNodes[i - offset];

      // Both nodes are empty, do nothing
      if (!oldChild && !newChild) {
        break;
      }

      // There is no new child, remove old
      if (!newChild) {
        oldChild && oldNode.removeChild(oldChild);
        i--;
        continue;
      }

      // There is no old child, add new
      if (!oldChild) {
        oldNode.appendChild(newChild);
        offset++;
        continue;
      }

      // Both nodes are the same, morph
      if (this.same(newChild, oldChild)) {
        morphed = this.walk(newChild, oldChild);
        if (morphed !== oldChild) {
          oldNode.replaceChild(morphed, oldChild);
          offset++;
        }
        continue;
      }

      if (this.options.reject?.(oldChild, newChild)) {
        newNode.removeChild(newChild);
        i--;
        continue;
      }

      // Try to find a matching node to reorder
      oldMatch = null;
      for (let j = i; j < oldNode.childNodes.length; j++) {
        const potentialOldNode = oldNode.childNodes[j];

        if (potentialOldNode && this.same(potentialOldNode, newChild)) {
          oldMatch = potentialOldNode;
          break;
        }
      }

      if (oldMatch) {
        morphed = this.walk(newChild, oldMatch);
        if (morphed !== oldMatch) offset++;
        oldNode.insertBefore(morphed, oldChild);
      } else if (!this.getNodeKey(newChild) && !this.getNodeKey(oldChild)) {
        // Safe to morph in-place if neither has a key
        morphed = this.walk(newChild, oldChild);
        if (morphed !== oldChild) {
          oldNode.replaceChild(morphed, oldChild);
          offset++;
        }
      } else {
        // Insert the node if we couldn't morph or find a match
        oldNode.insertBefore(newChild, oldChild);
        offset++;
      }
    }
  }
  same(a, b) {
    if (a.nodeType !== b.nodeType) return false;

    if (a.nodeType === Node.ELEMENT_NODE) {
      if (a instanceof Element && b instanceof Element && a.tagName !== b.tagName) return false;

      const aKey = this.getNodeKey(a);
      const bKey = this.getNodeKey(b);
      if (aKey && bKey && aKey !== bKey) return false;
    }

    if (a.nodeType === Node.TEXT_NODE && b.nodeType === Node.TEXT_NODE)
      return a.nodeValue?.trim() === b.nodeValue?.trim();
    if (a.nodeType === Node.COMMENT_NODE && b.nodeType === Node.COMMENT_NODE) return a.nodeValue === b.nodeValue;
    return true;
  }
}
class CtmLazyImg extends HTMLElement {
  constructor() {
    super();
    this.rootLazyMargin = this.dataset.rootLazyMargin || '0px 0px 150% 0px';
    this.rootMargin = this.dataset.rootMargin || '0px 0px 75% 0px';
    this.lazyPerformance();
    this.imgPerformance();
  }
  intersectionObserverItem({ rootMargin, fn }) {
    new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          typeof fn === 'function' && fn();
          observer.unobserve(this);
        }
      });
    }, { rootMargin }).observe(this);
  }
  lazyPerformance() {
    this.intersectionObserverItem({
      rootMargin: this.rootLazyMargin,
      fn: () => {
        const img = this.querySelector('img.hide');
        if (!img) return;
        img.classList.remove('hide');
      }
    })
  }
  imgPerformance() {
    this.intersectionObserverItem({
      rootMargin: this.rootMargin,
      fn: () => {
        const img = this.querySelector('img');
        if (!img) return;
        img.srcset = img.dataset.srcset;
      }
    })
  }
}
customElements.define('ctm-img-lazy', CtmLazyImg);

  window.CtmImageProcessor = window.CtmImageProcessor || class CtmImageProcessor {
    constructor({
      jpegQuality = 0.92,
      jpegBackground = '#ffffff',
      heicScriptUrl = 'https://cdn.shopify.com/s/files/1/0771/3734/6749/files/heic-to.min.js'
    } = {}) {
    this.jpegQuality = jpegQuality;
    this.jpegBackground = jpegBackground;
    this.heicScriptUrl = heicScriptUrl;
    this.heicModulePromise = null;
    this.supportedImageExtensions = ['jpg', 'jpeg', 'png', 'webp', 'heic'];
    this.supportedImageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    this.messages = {
      unsupportedType: 'Choose a JPG, PNG, WebP or HEIC image file.',
      processing: 'Preparing image…',
      conversionUnavailable: 'This image cannot be processed right now. Choose a JPG, PNG or WebP image.',
      conversionFailed: 'Unable to process this image. Choose a JPG, PNG or WebP image.',
      processingCancelled: 'Image processing was cancelled.'
    };
  }

  isSupported(file) {
    const extension = this.getFileExtension(file);
    const mimeType = file?.type?.toLowerCase() || '';
    return this.supportedImageTypes.includes(mimeType) || this.supportedImageExtensions.includes(extension);
  }

    needsJpegConversion(file) {
      return this.isHeic(file) || this.isWebp(file);
    }

    async normalizeUploadImageFile(file, options = {}) {
      const invalidFormatMessage = options.invalidFormatMessage || this.messages.unsupportedType;
      const signature = await this.readFileSignature(file, 64);

      let normalizedFile = file;
      if (this.isHeic(file, signature)) {
        normalizedFile = await this.convertHeicToJpeg(file, options.signal);
      }

      const normalizedSignature = await this.readFileSignature(normalizedFile, 64);

      if (this.isJpegSignatureBytes(normalizedSignature)) {
        return this.rebuildFileWithExtension(normalizedFile, 'jpg', 'image/jpeg');
      }

      if (this.isPngSignatureBytes(normalizedSignature)) {
        return this.rebuildFileWithExtension(normalizedFile, 'png', 'image/png');
      }

      if (this.isWebpSignatureBytes(normalizedSignature)) {
        return this.rebuildFileWithExtension(normalizedFile, 'webp', 'image/webp');
      }

      throw new Error(invalidFormatMessage);
    }

    async prepare(file, { signal } = {}) {
      const signature = await this.readFileSignature(file, 64);

      if (this.isWebp(file, signature)) {
        return this.convertBrowserImageToJpeg(file, signal);
      }

      if (this.isHeic(file, signature)) {
        return this.convertHeicToJpeg(file, signal);
      }

      return file;
    }

    async convertHeicToJpeg(file, signal) {
      this.throwIfAborted(signal);

      const signature = await this.readFileSignature(file, 64);
      if (!this.isHeic(file, signature)) return file;

      const heicTo = await this.loadHeicTo(signal);
      this.throwIfAborted(signal);

      if (typeof heicTo.isHeic === 'function') {
        const isHeic = await heicTo.isHeic(file);
        if (!isHeic) return file;
      }

      const convertedBlob = await heicTo.convert({
        blob: file,
        type: 'image/jpeg',
        quality: this.jpegQuality
      });

      this.throwIfAborted(signal);
      return this.createJpegFile(file, convertedBlob);
    }

  async convertBrowserImageToJpeg(file, signal) {
    this.throwIfAborted(signal);

    const objectUrl = URL.createObjectURL(file);

    try {
      const image = await this.loadImage(objectUrl);
      this.throwIfAborted(signal);

      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;

      const context = canvas.getContext('2d');
      if (!context || !canvas.width || !canvas.height) {
        throw new Error(this.messages.conversionFailed);
      }

      context.fillStyle = this.jpegBackground;
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      const convertedBlob = await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
            return;
          }
          reject(new Error(this.messages.conversionFailed));
        }, 'image/jpeg', this.jpegQuality);
      });

      this.throwIfAborted(signal);
      return this.createJpegFile(file, convertedBlob);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  createJpegFile(file, convertedBlob) {
    const convertedName = `${file.name.replace(/\.[^.]+$/, '') || 'converted-image'}.jpg`;
    return new File([convertedBlob], convertedName, {
      type: 'image/jpeg',
      lastModified: file.lastModified || Date.now()
    });
  }

    isHeic(file, signature = null) {
      const extension = this.getFileExtension(file);
      const mimeType = file?.type?.toLowerCase() || '';
      if (extension === 'heic' || extension === 'heif' || mimeType === 'image/heic' || mimeType === 'image/heif') {
        return true;
      }
      return Boolean(signature && this.isHeicSignatureBytes(signature));
    }

    isWebp(file, signature = null) {
      const extension = this.getFileExtension(file);
      const mimeType = file?.type?.toLowerCase() || '';
      if (extension === 'webp' || mimeType === 'image/webp') {
        return true;
      }
      return Boolean(signature && this.isWebpSignatureBytes(signature));
    }

    async readFileSignature(file, length = 64) {
      if (!file || typeof file.slice !== 'function' || !file.size) return new Uint8Array();

      try {
        const buffer = await file.slice(0, length).arrayBuffer();
        return new Uint8Array(buffer);
      } catch (error) {
        return new Uint8Array();
      }
    }

    isHeicSignatureBytes(bytes) {
      if (!bytes || bytes.length < 12) return false;
      if (this.bytesToAscii(bytes, 4, 8) !== 'ftyp') return false;

      const heicBrands = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs', 'heif', 'mif1', 'msf1'];
      for (let index = 8; index + 4 <= bytes.length; index += 4) {
        if (heicBrands.includes(this.bytesToAscii(bytes, index, index + 4))) return true;
      }

      return false;
    }

    isJpegSignatureBytes(bytes) {
      return Boolean(bytes && bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff);
    }

    isPngSignatureBytes(bytes) {
      const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
      return Boolean(bytes && bytes.length >= pngSignature.length && pngSignature.every((byte, index) => bytes[index] === byte));
    }

    isWebpSignatureBytes(bytes) {
      if (!bytes || bytes.length < 12) return false;
      return this.bytesToAscii(bytes, 0, 4) === 'riff' && this.bytesToAscii(bytes, 8, 12) === 'webp';
    }

    bytesToAscii(bytes, start, end) {
      let result = '';
      for (let index = start; index < end && index < bytes.length; index += 1) {
        result += String.fromCharCode(bytes[index]);
      }
      return result.toLowerCase();
    }

    rebuildFileWithExtension(file, extension, type) {
      const normalizedName = this.renameFileWithExtension(file?.name || 'upload', extension);
      if (file instanceof File && String(file.name || '').endsWith(`.${extension}`) && String(file.type || '').toLowerCase() === type) {
        return file;
      }

      return new File([file], normalizedName, {
        type,
        lastModified: file?.lastModified || Date.now()
      });
    }

    renameFileWithExtension(fileName, extension) {
      const baseName = String(fileName || 'upload').replace(/\.[^.]+$/, '') || 'upload';
      return `${baseName}.${extension}`;
    }

    getFileExtension(file) {
      const fileName = file?.name || '';
      const extensionIndex = fileName.lastIndexOf('.');
      return extensionIndex > -1 ? fileName.slice(extensionIndex + 1).toLowerCase() : '';
    }

  async loadHeicTo(signal) {
    const existingApi = this.resolveHeicToApi();
    if (existingApi) return existingApi;

    const heicModule = await this.loadHeicModule(signal);
    const loadedApi = this.resolveHeicToApi(heicModule);
    if (!loadedApi) {
      throw new Error(this.messages.conversionUnavailable);
    }

    return loadedApi;
  }

  async loadHeicModule(signal) {
    this.throwIfAborted(signal);
    this.heicModulePromise = this.heicModulePromise || import(this.heicScriptUrl);

    try {
      const heicModule = await this.heicModulePromise;
      this.throwIfAborted(signal);
      return heicModule;
    } catch (error) {
      this.heicModulePromise = null;
      if (error?.name === 'AbortError') throw error;
      throw new Error(this.messages.conversionUnavailable);
    }
  }

  resolveHeicToApi(heicModule = null) {
    const candidates = [
      heicModule,
      window.heicTo,
      window.HeicTo
    ].filter(Boolean);

    for (const candidate of candidates) {
      const convert = typeof candidate === 'function' ? candidate : candidate.heicTo || candidate.default || candidate.convert;
      if (typeof convert === 'function') {
        return {
          convert: convert.bind(candidate),
          isHeic: typeof candidate.isHeic === 'function' ? candidate.isHeic.bind(candidate) : null
        };
      }
    }

    return null;
  }

  loadImage(source) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = source;
    });
  }

  throwIfAborted(signal) {
    if (signal?.aborted) throw this.createAbortError();
  }

  createAbortError() {
    return new DOMException(this.messages.processingCancelled, 'AbortError');
  }
};

window.CtmDesignImageProcessor = window.CtmDesignImageProcessor || window.CtmImageProcessor;
